
import React, { useState, useEffect } from 'react';
import { X, Trophy, Calendar, MapPin, Star, Users, Ticket, Music, Heart, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const SportsModal = ({ isOpen, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('cricket');

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sportsCategories = {
    cricket: {
      title: 'Cricket Experiences',
      icon: Trophy,
      gradient: 'from-green-500 to-emerald-600',
      description: 'Where passion meets privilege. We craft cricket journeys that go far beyond the scoreboard — moments that put you in the heart of the action while surrounding you with comfort, style, and exclusivity. Whether it\'s the roar of a packed stadium or the quiet thrill of holding a signed bat, every detail is designed to make you feel like you belong in the game\'s inner circle.',
      disclaimer: 'Centre Stage Sports & Celebrities is an independent provider of curated sports experiences and is not affiliated with or endorsed by the ICC, BCCI, IPL, or any other event organiser unless explicitly stated.',
      features: [
        'Premium seating with exceptional sightlines',
        'Invitations to select fan gatherings and cricket‑themed hospitality lounges',
        'Curated opportunities to meet cricket personalities (subject to availability)',
        'Specially sourced memorabilia to take home a piece of the moment'
      ]
    },
    tennis: {
      title: 'Tennis Majors',
      icon: Trophy,
      gradient: 'from-yellow-500 to-orange-600',
      description: 'Where elegance meets intensity. We create tennis experiences that let you savour every rally, every roar from the crowd, and every champagne toast in between. It\'s not just about watching the match — it\'s about feeling the prestige of the occasion, surrounded by comfort and style that matches the sport\'s sophistication.',
      disclaimer: 'Centre Stage Sports & Celebrities is an independent provider of curated sports experiences and is not affiliated with or endorsed by the ATP, WTA, ITF, or any tournament organiser unless explicitly stated.',
      features: [
        'Premium seating with exceptional court views',
        'Invitations to select tennis‑themed hospitality lounges',
        'Guided venue tours and curated behind‑the‑scenes moments (subject to availability)',
        'Specially sourced memorabilia to commemorate your day'
      ]
    },
    formula1: {
      title: 'Formula 1 Racing',
      icon: Trophy,
      gradient: 'from-red-500 to-pink-600',
      description: 'Speed, style, and the sound of pure adrenaline. We design race‑weekend experiences that put you at the heart of motorsport\'s most glamorous stage. Every detail is considered — from the moment you arrive at the circuit to the last champagne toast after the chequered flag. It\'s not just about watching cars fly past; it\'s about living the theatre, the tension, and the thrill that only Formula 1 can deliver.',
      disclaimer: 'Centre Stage Sports & Celebrities is an independent provider of curated sports experiences and is not affiliated with or endorsed by Formula One Management, FIA, or any race organiser unless explicitly stated.',
      features: [
        'Premium trackside hospitality with unbeatable views',
        'Invitations to select paddock or pit lane experiences (subject to availability)',
        'Curated opportunities to meet motorsport personalities',
        'Exclusive race‑weekend social events and after‑parties'
      ]
    },
    football: {
      title: 'Football Championships',
      icon: Trophy,
      gradient: 'from-blue-500 to-indigo-600',
      description: 'Where passion fills the air and every seat feels like the best in the house. We create football experiences that capture the electricity of the crowd, the drama on the pitch, and the luxury of being treated like a VIP from the moment you arrive. It\'s about more than the match — it\'s about immersing yourself in the culture, the rivalries, and the unforgettable moments that make the beautiful game truly beautiful.',
      disclaimer: 'Centre Stage Sports & Celebrities is an independent provider of curated sports experiences and is not affiliated with or endorsed by FIFA, UEFA, AFC, or any football club or tournament organiser unless explicitly stated.',
      features: [
        'VIP seating in select stadium sections with exceptional views',
        'Invitations to themed hospitality lounges and fan celebrations',
        'Curated opportunities to meet football legends (subject to availability)',
        'Specially sourced memorabilia to remember the occasion'
      ]
    },
    olympics: {
      title: 'Olympic Games',
      icon: Trophy,
      gradient: 'from-purple-500 to-violet-600',
      description: 'The world\'s greatest stage, experienced your way. We bring you closer to the spirit of the Games — the anticipation before the starting gun, the pride in the medal ceremony, the shared energy of fans from every corner of the globe. Every detail is designed to let you enjoy the spectacle in comfort, style, and with a sense of occasion worthy of the moment.',
      disclaimer: 'Centre Stage Sports & Celebrities is an independent provider of curated sports experiences and is not affiliated with or endorsed by the International Olympic Committee (IOC) or any National Olympic Committee unless explicitly stated.',
      features: [
        'Premium seating for select events with exceptional views',
        'Access to themed hospitality zones for an elevated match‑day atmosphere',
        'Curated cultural and city experiences alongside the sporting action',
        'Specially sourced commemorative items to mark the occasion'
      ]
    }
  };

  const currentCategory = sportsCategories[activeCategory];

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-1 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg sm:rounded-3xl max-w-6xl w-full h-[98vh] sm:h-[95vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-2 sm:p-6 text-white relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 p-1 sm:p-2 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-4 h-4 sm:w-6 sm:h-6" />
          </button>
          <div className="flex items-center gap-1 sm:gap-4 pr-6 sm:pr-12">
            <Trophy className="w-4 h-4 sm:w-10 sm:h-10 text-white flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
            <div className="min-w-0">
              <h2 className="text-sm sm:text-3xl font-bold leading-tight">Luxury Sporting Experiences</h2>
              <p className="text-white/90 text-xs sm:text-lg mt-0 sm:mt-1 hidden sm:block">Premium access to the world's greatest sporting events</p>
            </div>
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex flex-1 min-h-0">
          {/* Mobile: Categories as horizontal tabs */}
          <div className="sm:hidden w-full flex flex-col">
            {/* Category tabs */}
            <div className="flex overflow-x-auto bg-gray-50 border-b border-gray-200 flex-shrink-0">
              {Object.entries(sportsCategories).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex-shrink-0 px-3 py-2 text-xs font-medium transition-all duration-200 ${
                    activeCategory === key
                      ? `bg-gradient-to-r ${category.gradient} text-white`
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <category.icon className="w-3 h-3" style={{strokeWidth: '1.5', fill: 'none'}} />
                    <span className="whitespace-nowrap">{category.title}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Mobile content */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 bg-gradient-to-r ${currentCategory.gradient} rounded-lg flex items-center justify-center`}>
                    <currentCategory.icon className="w-3 h-3 text-white" style={{strokeWidth: '1.5', fill: 'none'}} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{currentCategory.title}</h3>
                  </div>
                </div>
              </div>

              {/* Mobile Description */}
              <div className="mb-4">
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  {currentCategory.description}
                </p>
                
                <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-gray-400">
                  <p className="text-xs text-gray-600 italic">
                    {currentCategory.disclaimer}
                  </p>
                </div>
              </div>

              {/* Mobile Features */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Your experience may include:</h4>
                <div className="space-y-2">
                  {currentCategory.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <ArrowRight className={`w-3 h-3 mt-1 flex-shrink-0 ${
                        activeCategory === 'cricket' ? 'text-green-600' :
                        activeCategory === 'tennis' ? 'text-yellow-600' :
                        activeCategory === 'formula1' ? 'text-red-600' :
                        activeCategory === 'football' ? 'text-blue-600' :
                        activeCategory === 'olympics' ? 'text-purple-600' : 'text-blue-600'
                      }`} style={{strokeWidth: '1.5', fill: 'none'}} />
                      <p className="text-gray-700 text-sm">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Services */}
              <div className="mb-4 p-3 bg-white rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                  <Star className="w-3 h-3 text-yellow-500" style={{strokeWidth: '1.5', fill: 'none'}} />
                  Premium Services
                </h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <Ticket className="w-2 h-2" style={{strokeWidth: '1.5', fill: 'none'}} />
                    VIP Ticketing
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="w-2 h-2" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Group Packages
                  </li>
                  <li className="flex items-center gap-2">
                    <Calendar className="w-2 h-2" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Multi-Event Access
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="w-2 h-2" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Luxury Hospitality
                  </li>
                </ul>
              </div>

              {/* Mobile Contact CTA */}
              <div className="mt-4 p-4 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl text-white">
                <div className="text-center">
                  <h4 className="text-sm font-bold mb-1">Ready to Experience Excellence?</h4>
                  <p className="text-gray-300 mb-3 text-xs">Contact our specialists to create your bespoke sporting experience</p>
                  <Button 
                    onClick={onClose}
                    className="bg-white text-gray-900 hover:bg-gray-100 transition-colors px-6 text-sm"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex flex-1 min-h-0">
            {/* Sidebar */}
            <div className="w-80 bg-gray-50 p-6 overflow-y-auto flex-shrink-0 border-r border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-base">Experience Categories</h3>
              <div className="space-y-2">
                {Object.entries(sportsCategories).map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-200 ${
                      activeCategory === key
                        ? `bg-gradient-to-r ${category.gradient} text-white shadow-lg`
                        : 'bg-white hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <category.icon className="w-5 h-5 flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
                      <span className="font-medium text-base truncate">{category.title}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Services Highlight */}
              <div className="mt-8 p-4 bg-white rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" style={{strokeWidth: '1.5', fill: 'none'}} />
                  Premium Services
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <Ticket className="w-3 h-3" style={{strokeWidth: '1.5', fill: 'none'}} />
                    VIP Ticketing
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="w-3 h-3" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Group Packages
                  </li>
                  <li className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Multi-Event Access
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="w-3 h-3" style={{strokeWidth: '1.5', fill: 'none'}} />
                    Luxury Hospitality
                  </li>
                </ul>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${currentCategory.gradient} rounded-xl flex items-center justify-center`}>
                    <currentCategory.icon className="w-6 h-6 text-white" style={{strokeWidth: '1.5', fill: 'none'}} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{currentCategory.title}</h3>
                    <p className="text-gray-600 text-base">World-class experiences await</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  {currentCategory.description}
                </p>
                
                <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-gray-400">
                  <p className="text-sm text-gray-600 italic">
                    {currentCategory.disclaimer}
                  </p>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Your experience may include:</h4>
                <div className="space-y-3">
                  {currentCategory.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <ArrowRight className={`w-5 h-5 mt-1 flex-shrink-0 ${
                        activeCategory === 'cricket' ? 'text-green-600' :
                        activeCategory === 'tennis' ? 'text-yellow-600' :
                        activeCategory === 'formula1' ? 'text-red-600' :
                        activeCategory === 'football' ? 'text-blue-600' :
                        activeCategory === 'olympics' ? 'text-purple-600' : 'text-blue-600'
                      }`} style={{strokeWidth: '1.5', fill: 'none'}} />
                      <p className="text-gray-700">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact CTA */}
              <div className="mt-8 p-6 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl text-white">
                <div className="text-center">
                  <h4 className="text-xl font-bold mb-2">Ready to Experience Excellence?</h4>
                  <p className="text-gray-300 mb-4">Contact our specialists to create your bespoke sporting experience</p>
                  <Button 
                    onClick={onClose}
                    className="bg-white text-gray-900 hover:bg-gray-100 transition-colors px-8"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
